import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, authQuery, API, downloadUrl, normalizeLangCode } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { ArrowLeft, Upload, Trash2, Copy, Smartphone, Send, GraduationCap } from "lucide-react";
import LodgeDatePicker from "../components/LodgeDatePicker";
import PassportSelect from "../components/PassportSelect";
import SignaturePad from "../components/SignaturePad";
import { fetchObedienceType, isSisterMember } from "../lib/gender";

const GRADES = ["Entered Apprentice", "Fellow Craft", "Master Mason", "Honorary"];
const STATUSES = ["Active", "Inactive", "Suspended", "Deceased", "Dormant", "Excluded"];

// Standard 17 offices in a Masonic lodge, listed from the highest office
// (Maestru Venerabil) down to Acoperitor Exterior. Each entry carries an
// English translation surfaced when the UI runs in EN locale; the stored
// value (in MongoDB) remains the Romanian canonical string so we don't
// fragment the data across languages.
const LODGE_OFFICES = [
  // Symbolic lodge officers
  "Maestru Venerabil",
  "Prim Supraveghetor",
  "Al Doilea Supraveghetor",
  "Orator",
  "Secretar",
  "Trezorier",
  "Spitalier",
  "Maestru de Ceremonii",
  "Expert",
  "Arhivar-Bibliotecar",
  "Maestru de Banchete",
  "Acoperitor Interior",
  "Acoperitor Exterior",
  "Maestru Venerabil din Trecut",
  // Grand Lodge / Obedience officers
  "Mare Maestru",
  "Mare Maestru din Trecut",
  "Mare Secretar",
  "Mare Trezorier",
  "Mare Cancelar",
  "Mare Prim Supraveghetor",
  "Mare al Doilea Supraveghetor",
  "Mare Maestru de Ceremonii",
  "Mare Expert",
  "Mare Acoperitor Interior",
  "Mare Acoperitor Exterior",
  "Mare Ospitalier",
  // Honorary
  "Membru Onorific",
];

// Map office name (DB key, Romanian) → i18n key so labels auto-localize.
const LODGE_OFFICE_TKEY = {
  "Maestru Venerabil": "memberProfile.office_maestru_venerabil",
  "Prim Supraveghetor": "memberProfile.office_prim_supraveghetor",
  "Al Doilea Supraveghetor": "memberProfile.office_al_doilea_supraveghetor",
  "Orator": "memberProfile.office_orator",
  "Secretar": "memberProfile.office_secretar",
  "Trezorier": "memberProfile.office_trezorier",
  "Spitalier": "memberProfile.office_spitalier",
  "Maestru de Ceremonii": "memberProfile.office_maestru_ceremonii",
  "Expert": "memberProfile.office_expert",
  "Arhivar-Bibliotecar": "memberProfile.office_arhivar_bibliotecar",
  "Maestru de Banchete": "memberProfile.office_maestru_banchete",
  "Acoperitor Interior": "memberProfile.office_acoperitor_interior",
  "Acoperitor Exterior": "memberProfile.office_acoperitor_exterior",
  "Maestru Venerabil din Trecut": "memberProfile.office_maestru_venerabil_din_trecut",
  "Mare Maestru": "memberProfile.office_mare_maestru",
  "Mare Maestru din Trecut": "memberProfile.office_mare_maestru_din_trecut",
  "Mare Secretar": "memberProfile.office_mare_secretar",
  "Mare Trezorier": "memberProfile.office_mare_trezorier",
  "Mare Cancelar": "memberProfile.office_mare_cancelar",
  "Mare Prim Supraveghetor": "memberProfile.office_mare_prim_supraveghetor",
  "Mare al Doilea Supraveghetor": "memberProfile.office_mare_al_doilea_supraveghetor",
  "Mare Maestru Adjunct": "memberProfile.office_mare_maestru_adjunct",
  "Mare Orator": "memberProfile.office_mare_orator",
  "Mare Maestru de Ceremonii": "memberProfile.office_mare_maestru_ceremonii",
  "Mare Expert": "memberProfile.office_mare_expert",
  "Mare Acoperitor Interior": "memberProfile.office_mare_acoperitor_interior",
  "Mare Acoperitor Exterior": "memberProfile.office_mare_acoperitor_exterior",
  "Mare Ospitalier": "memberProfile.office_mare_ospitalier",
  "Membru Onorific": "memberProfile.office_membru_onorific",
};

// Full Grand-Lodge officer list — mirrors backend GRAND_OFFICE_TITLES.
const GRAND_OFFICES = [
  "Mare Maestru",
  "Mare Maestru Adjunct",
  "Mare Maestru din Trecut",
  "Mare Prim Supraveghetor",
  "Mare al Doilea Supraveghetor",
  "Mare Orator",
  "Mare Secretar",
  "Mare Trezorier",
  "Mare Cancelar",
  "Mare Expert",
  "Mare Maestru de Ceremonii",
  "Mare Ospitalier",
  "Mare Acoperitor Interior",
  "Mare Acoperitor Exterior",
];

const ALL_OFFICES = [...new Set([...LODGE_OFFICES, ...GRAND_OFFICES])];

const emptyForm = {
  first_name: "", last_name: "", grade: "Entered Apprentice", degree: 1, status: "Active",
  is_private: false, date_of_birth: "", date_initiated: "", date_promoted: "",
  passport_number: "", address: "", phone: "", email: "", profession: "", sex: "",
  photo_url: "", notes: "", additional_info: "", lodge_office: "", grand_office: "",
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(13,27,42,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "1rem",
};
const modalCard = {
  background: "#fdf9ec",
  border: "1px solid #c9a84c",
  padding: "1.4rem 1.6rem",
  width: "100%",
  boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
};

function OfficeHistoryPanel({ memberId, items, isAdmin, onChanged }) {
  const { t } = useTranslation();
  const [office, setOffice] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const sorted = [...(items || [])].sort((a, b) => (b.start || "").localeCompare(a.start || ""));
  const label = (o) => (LODGE_OFFICE_TKEY[o] ? t(LODGE_OFFICE_TKEY[o]) : o);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/members/${memberId}/office-history`, { office, start, end: end || null });
      onChanged(data.items);
      setOffice(""); setStart(""); setEnd("");
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  const remove = async (entryId) => {
    try {
      const { data } = await api.delete(`/members/${memberId}/office-history/${entryId}`);
      onChanged(data.items);
    } catch { /* ignore */ }
  };
  return (
    <div className="panel" data-testid="office-history-panel">
      <div className="panel-head">{t("memberProfile.officeHistory", "Istoric funcții")}</div>
      <div className="panel-body">
        {sorted.length === 0 && (
          <p className="kv-label" data-testid="office-history-empty">{t("memberProfile.officeHistoryEmpty", "Nicio funcție înregistrată încă.")}</p>
        )}
        {sorted.map((e) => (
          <div key={e.id} className="kv-row" data-testid="office-history-row">
            <span className="kv-label">{label(e.office)}</span>
            <span className="kv-value" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {(e.start || "").slice(0, 4)} — {e.end ? (e.end || "").slice(0, 4) : t("memberProfile.officeHistoryPresent", "prezent")}
              {isAdmin && (
                <button type="button" onClick={() => remove(e.id)} data-testid="office-history-delete-btn"
                        title={t("common.delete", "Șterge")}
                        style={{ background: "none", border: "none", color: "#a33", cursor: "pointer", fontSize: "0.85rem" }}>✕</button>
              )}
            </span>
          </div>
        ))}
        {isAdmin && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <select className="input-lodge" style={{ flex: "2 1 190px" }} value={office}
                    onChange={(e) => setOffice(e.target.value)} data-testid="office-history-office-select">
              <option value="">{t("memberProfile.no_office")}</option>
              {ALL_OFFICES.map((o) => <option key={o} value={o}>{label(o)}</option>)}
            </select>
            <input className="input-lodge" style={{ flex: "1 1 90px" }} maxLength={10}
                   placeholder={t("memberProfile.officeHistoryStart", "Anul început")} value={start}
                   onChange={(e) => setStart(e.target.value)} data-testid="office-history-start-input" />
            <input className="input-lodge" style={{ flex: "1 1 90px" }} maxLength={10}
                   placeholder={t("memberProfile.officeHistoryEnd", "Anul încheiat")} value={end}
                   onChange={(e) => setEnd(e.target.value)} data-testid="office-history-end-input" />
            <button type="button" className="btn-outline-gold" onClick={add} disabled={busy || !office || !start}
                    data-testid="office-history-add-btn">
              {t("memberProfile.officeHistoryAdd", "Adaugă mandat")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MemberProfilePage({ mode = "view" }) {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const isNew = id === "new" || mode === "new";
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [grades, setGrades] = useState([]);
  const [availablePassports, setAvailablePassports] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [obedienceType, setObedienceType] = useState("regulara");
  useEffect(() => { fetchObedienceType().then(setObedienceType); }, []);
  const memberIsSister = isSisterMember(form, obedienceType);

  useEffect(() => {
    (async () => {
      try {
        const [gRes, pRes] = await Promise.all([
          api.get("/reference/grades"),
          isAdmin ? api.get("/passport-numbers/available") : Promise.resolve({ data: [] }),
        ]);
        setGrades(gRes.data);
        setAvailablePassports(pRes.data);
      } catch { /* ignore */ }
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setEditing(true);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/members/${id}`);
        setForm({ ...emptyForm, ...data });
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [id, isNew]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.storage_path }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [newMemberPrompt, setNewMemberPrompt] = useState(null);
  const [postSaveCreds, setPostSaveCreds] = useState(null);
  const [issuingNewMember, setIssuingNewMember] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  const submitDigitalSignature = async (dataURL) => {
    if (!id || isNew) return;
    setUploadingSig(true);
    try {
      const { data } = await api.post(`/upload/signature/${id}/digital`, { png_base64: dataURL });
      setForm((f) => ({ ...f, signature_url: data.storage_path }));
      setShowSignaturePad(false);
      toast.success(t("memberProfile.signature_captured"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploadingSig(false);
    }
  };

  // Upload + auto-vectorise the brother's signature. The file goes to
  // /api/upload/signature/{member_id} which strips the white background
  // (just like the lodge stamp pipeline). The resulting transparent PNG
  // is wired to `signature_url` and is used automatically when this
  // member is selected as an officer on a certificate template.
  const uploadSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !id || isNew) return;
    setUploadingSig(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload/signature/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, signature_url: data.storage_path }));
      toast.success(t("memberProfile.signature_saved"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploadingSig(false);
    }
  };

  const removeSignature = async () => {
    if (!id || isNew) return;
    if (!window.confirm(t("memberProfile.remove_this_signature"))) return;
    setForm((f) => ({ ...f, signature_url: null }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isNew) {
        const { data } = await api.post("/members", form);
        if (data.duplicate && data.transfer_request) {
          // Confirm path already handled (shouldn't happen on first try)
          toast.success(t("transfers.requestSent", "Cerere de transfer trimisă"));
          navigate("/members", { replace: true });
          return;
        }
        toast.success(t("members.addedSuccess"));
        // If the new member has an email, immediately offer to issue
        // mobile credentials so the admin doesn't forget / confuse with
        // another brother.
        if ((form.email || "").trim()) {
          setNewMemberPrompt({
            id: data.id,
            firstName: form.first_name,
            lastName: form.last_name,
            email: form.email.trim(),
          });
        } else {
          navigate(`/members/${data.id}`, { replace: true });
        }
      } else {
        await api.put(`/members/${id}`, form);
        toast.success(t("members.updatedSuccess"));
        setEditing(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "EXISTING_MEMBER") {
        // Surface the duplicate to the admin. Saving is blocked — they must
        // open the Transfers page to request a transfer instead.
        setPendingTransfer({ detail, formSnapshot: { ...form } });
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const goToTransfers = () => {
    setPendingTransfer(null);
    // The Transfers page accepts an optional `prefill` query so it can
    // pre-select the candidate member.
    const m = pendingTransfer?.detail?.member;
    const id = m?.id || "";
    navigate(`/transfers${id ? `?member=${id}` : ""}`);
  };

  const remove = async () => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("members.deleteConfirm"),
    });
    if (!ok) return;
    try {
      await api.delete(`/members/${id}`);
      toast.success(t("members.deletedSuccess"));
      navigate("/members", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="member-profile-page">
      <button className="btn-outline-gold mb-4" onClick={() => navigate("/members")} data-testid="back-to-members-btn">
        <ArrowLeft size={14} /> {t("common.back")}
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">
            {isNew ? t("members.newMember") : `${form.first_name} ${form.last_name}`}
          </h1>
          {!isNew && <p className="page-subtitle">{t("members.title")}</p>}
        </div>
        {!isNew && !editing && isAdmin && (
          <div className="flex gap-2">
            <button className="btn-outline-gold" onClick={() => setEditing(true)} data-testid="edit-member-btn">{t("common.edit")}</button>
            <button className="btn-danger" onClick={remove} data-testid="delete-member-btn"><Trash2 size={12} /> {t("common.delete")}</button>
          </div>
        )}
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      {editing ? (
        <form onSubmit={save} data-testid="member-form">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="panel">
                <div className="panel-head">{t("members.photo")}</div>
                <div className="panel-body flex flex-col items-center">
                  {form.photo_url ? (
                    <img src={`${API}/photo?path=${encodeURIComponent(form.photo_url)}${authQuery().replace("?", "&")}`} alt="" style={{ width: 180, height: 180, objectFit: "cover", border: "1px solid #c9a84c" }} />
                  ) : (
                    <div style={{ width: 180, height: 180, background: "#f5f0e8", border: "1px dashed #c9a84c", display: "flex", alignItems: "center", justifyContent: "center", color: "#9a7530", fontFamily: "Cinzel, serif", fontSize: "0.8rem", letterSpacing: "0.14em" }}>
                      NO PHOTO
                    </div>
                  )}
                  <label className="btn-outline-gold mt-4 cursor-pointer flex items-center gap-2">
                    <Upload size={12} /> {uploading ? "..." : t("common.upload")}
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" style={{ display: "none" }} />
                  </label>
                  <div className="kv-label mt-2" style={{ fontSize: "0.65rem" }}>JPG, PNG (max 5MB)</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="panel">
                <div className="panel-head">{t("members.personal")}</div>
                <div className="panel-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-lodge">{t("members.firstName")}</label>
                    <input className="input-lodge" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="input-first-name" />
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.lastName")}</label>
                    <input className="input-lodge" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="input-last-name" />
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.dateOfBirth")}</label>
                    <LodgeDatePicker value={form.date_of_birth || ""} onChange={(v) => setForm({ ...form, date_of_birth: v })} placeholder={t("members.selectDob")} testId="input-dob" />
                  </div>
                  {obedienceType === "mixta" && (
                    <div>
                      <label className="label-lodge">{t("members.sex", "Sex")}</label>
                      <select
                        className="input-lodge"
                        value={form.sex || ""}
                        onChange={(e) => setForm({ ...form, sex: e.target.value })}
                        data-testid="input-member-sex"
                      >
                        <option value="">—</option>
                        <option value="M">{t("members.sexMale", "Masculin (Frate)")}</option>
                        <option value="F">{t("members.sexFemale", "Feminin (Soră)")}</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="label-lodge">{t("members.address", "Adresă")}</label>
                    <input className="input-lodge" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-lodge">{t("common.phone")}</label>
                    <input className="input-lodge" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-lodge">{t("common.email")}</label>
                    <input type="email" className="input-lodge" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label-lodge">{t("members.profession")}</label>
                    <input className="input-lodge" value={form.profession || ""} onChange={(e) => setForm({ ...form, profession: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="panel">
                <div className="panel-head">{t("members.masonic")}</div>
                <div className="panel-body grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="label-lodge">{t("members.degree")}</label>
                    <select
                      className="input-lodge"
                      value={form.degree || 1}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        const found = grades.find((g) => g.degree === n);
                        // Persist the canonical English name in the DB so
                        // analytics + cross-lang queries keep working. The
                        // localized label is only used in the UI.
                        const label = found ? found.name_en : "";
                        // Reset `date_promoted` whenever the grade changes
                        // so the admin enters a date specific to the NEW
                        // grade. The backend routes that date into the
                        // per-grade slot (date_promoted_companion /
                        // date_promoted_master) based on the new degree —
                        // previous milestones are preserved server-side.
                        // Stay no-op when the same degree is re-selected.
                        const next = { ...form, degree: n, grade: label };
                        if (n !== (form.degree || 1)) {
                          next.date_promoted = "";
                        }
                        setForm(next);
                      }}
                      data-testid="input-grade"
                    >
                      {grades.map((g) => {
                        // i18next.language can be "fr-FR" / "en-GB" — strip
                        // the region suffix before lookup so we always hit
                        // the per-language column on the API row.
                        const lng = normalizeLangCode(i18n.language || "ro");
                        const localized = g[`name_${lng}`] || g[`name_${lng.slice(0, 2)}`] || g.name_en || g.name_ro;
                        return (
                          <option key={g.degree} value={g.degree}>
                            {g.degree}° — {localized}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.passportNumber", "Pașaport")}</label>
                    <PassportSelect
                      value={form.passport_number || ""}
                      options={availablePassports}
                      onChange={(code) => setForm({ ...form, passport_number: code })}
                      placeholder={`— ${t("passports.notAssigned", "Neatribuit")} —`}
                      testId="input-passport-number"
                    />
                    {availablePassports.length === 0 && !form.passport_number && (
                      <p className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.3rem", color: "#9a7530" }}>
                        {t("memberProfile.no_available_passports_ask_superadmin")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-lodge">{t("common.status")}</label>
                    <select className="input-lodge" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      {STATUSES.map((s) => {
                        const key = {
                          Active: "members.status_active",
                          Inactive: "members.status_inactive",
                          Suspended: "members.status_suspended",
                          Deceased: "members.status_deceased",
                          Dormant: "members.status_dormant",
                          Excluded: "members.status_excluded",
                        }[s];
                        return <option key={s} value={s}>{key ? t(key, s) : s}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.dateInitiated")}</label>
                    <LodgeDatePicker value={form.date_initiated || ""} onChange={(v) => setForm({ ...form, date_initiated: v })} placeholder={t("members.selectInit")} testId="input-date-initiated" />
                  </div>
                  <div>
                    <label className="label-lodge">
                      {form.degree === 2
                        ? t("members.datePromotedCompanion", "Data ridicării la Calfă")
                        : form.degree === 3
                          ? t("members.datePromotedMaster", "Data ridicării la Maestru")
                          : t("members.datePromoted")}
                    </label>
                    <LodgeDatePicker
                      value={form.date_promoted || ""}
                      onChange={(v) => setForm({ ...form, date_promoted: v })}
                      placeholder={t("members.selectProm")}
                      testId="input-date-promoted"
                      disabled={form.degree === 1}
                    />
                    {form.degree === 1 ? (
                      <p className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.3rem", color: "#9a7530" }}>
                        {t("members.datePromotedHintApprentice", "Disponibil după ridicarea la Calfă (grad 2).")}
                      </p>
                    ) : (
                      <p className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.3rem", color: "#9a7530" }}>
                        {t("members.datePromotedHint", "Schimbarea gradului resetează această dată — completează-o pentru noul grad.")}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.lodgeOffice",t("memberProfile.lodge_officer"))}</label>
                    <select
                      className="input-lodge"
                      value={form.lodge_office || ""}
                      onChange={(e) => setForm({ ...form, lodge_office: e.target.value })}
                      data-testid="input-lodge-office"
                    >
                      <option value="">{t("members.lodgeOfficeNone",t("memberProfile.no_office"))}</option>
                      {LODGE_OFFICES.map((o) => (
                        <option key={o} value={o}>
                          {LODGE_OFFICE_TKEY[o] ? t(LODGE_OFFICE_TKEY[o]) : o}
                        </option>
                      ))}
                    </select>
                  </div>
                  {user?.role === "admin" && !user?.child_lodge_id && (
                    <div>
                      <label className="label-lodge">{t("members.grandOffice", "Funcție în Marea Lojă")}</label>
                      <select
                        className="input-lodge"
                        value={form.grand_office || ""}
                        onChange={(e) => setForm({ ...form, grand_office: e.target.value })}
                        data-testid="input-grand-office"
                      >
                        <option value="">{t("members.lodgeOfficeNone", t("memberProfile.no_office"))}</option>
                        {GRAND_OFFICES.map((o) => (
                          <option key={o} value={o}>
                            {LODGE_OFFICE_TKEY[o] ? t(LODGE_OFFICE_TKEY[o]) : o}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Officer signature — auto-applied on certificate
                      templates whenever this brother holds a `lodge_office`.
                      Only available on existing members (need an `id` to
                      persist). The image is auto-vectorised: white
                      background → transparent. */}
                  {!isNew ? (
                    <div className="md:col-span-2" data-testid="member-signature-section">
                      <label className="label-lodge">
                        {t("memberProfile.officer_signature_auto_vectorised")}
                      </label>
                      <p className="kv-label" style={{ fontSize: "0.66rem", marginTop: 2, marginBottom: 8 }}>
                        {t("memberProfile.photograph_the_signature_on_plain")}
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        {form.signature_url ? (
                          <img
                            src={`${API}/photo?path=${encodeURIComponent(form.signature_url)}${authQuery().replace("?", "&")}`}
                            alt="signature"
                            style={{
                              width: 220, height: 80, objectFit: "contain",
                              border: "1px solid #c9a84c", borderRadius: 6,
                              background: "repeating-conic-gradient(#f0e9d0 0% 25%, #fff 0% 50%) 50% / 12px 12px",
                            }}
                            data-testid="member-signature-preview"
                          />
                        ) : (
                          <div style={{
                            width: 220, height: 80,
                            border: "1px dashed #c9a84c", borderRadius: 6,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#9a7530", fontSize: "0.7rem", fontStyle: "italic",
                          }}>
                            {t("memberProfile.no_signature_uploaded")}
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          <label className="lodge-edit-btn-gold cursor-pointer" data-testid="member-signature-upload-btn">
                            <Upload size={12} style={{ display: "inline", marginRight: "0.3rem" }} />
                            {uploadingSig
                              ? t("memberProfile.vectorising")
                              : (form.signature_url
                                ? t("memberProfile.replace")
                                : t("memberProfile.upload"))}
                            <input type="file" accept="image/*,application/pdf" onChange={uploadSignature} style={{ display: "none" }} disabled={uploadingSig} data-testid="member-signature-input" />
                          </label>
                          <button
                            type="button"
                            className="btn-outline-gold"
                            style={{ fontSize: "0.7rem" }}
                            onClick={() => setShowSignaturePad(true)}
                            data-testid="member-signature-draw-btn"
                          >
                            ✍ {t("memberProfile.sign_now")}
                          </button>
                          {form.signature_url ? (
                            <button
                              type="button"
                              className="btn-outline-gold"
                              style={{ borderColor: "#7c2a2a", color: "#7c2a2a", fontSize: "0.7rem" }}
                              onClick={removeSignature}
                              data-testid="member-signature-remove-btn"
                            >
                              {t("memberProfile.remove")}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2 mt-2">
                      <input type="checkbox" checked={!!form.is_private} onChange={(e) => setForm({ ...form, is_private: e.target.checked })} data-testid="input-private" />
                      <span>{t("members.isPrivate")}</span>
                    </label>
                  </div>
                  {form.status && form.status !== "Active" && form.status !== "Honorary" && (
                    <div className="md:col-span-2">
                      <label className="label-lodge">{t("attendance.statusReason")}</label>
                      <textarea
                        rows={2}
                        className="input-lodge"
                        value={form.status_reason || ""}
                        onChange={(e) => setForm({ ...form, status_reason: e.target.value })}
                        data-testid="input-status-reason"
                        placeholder={t("attendance.statusReason")}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="panel">
                <div className="panel-head">{t("members.additionalInfo")}</div>
                <div className="panel-body space-y-4">
                  <div>
                    <label className="label-lodge">{t("common.notes")}</label>
                    <textarea rows={3} className="input-lodge" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </div>
                  <div>
                    <label className="label-lodge">{t("members.additionalInfo")}</label>
                    <textarea rows={3} className="input-lodge" value={form.additional_info || ""} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} />
                  </div>
                </div>
              </div>

              {!isNew && form.id && <MemberDocsPanel memberId={form.id} />}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            {!isNew && <button type="button" className="btn-outline-gold" onClick={() => setEditing(false)}>{t("common.cancel")}</button>}
            <button type="submit" className="btn-primary-gold" disabled={saving} data-testid="save-member-btn">
              {saving ? "..." : t("common.save")}
            </button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div>
            <div className="panel">
              <div className="panel-head">{t("members.photo")}</div>
              <div className="panel-body flex flex-col items-center">
                {form.photo_url ? (
                  <img src={`${API}/photo?path=${encodeURIComponent(form.photo_url)}${authQuery().replace("?", "&")}`} alt="" style={{ width: 180, height: 180, objectFit: "cover", border: "1px solid #c9a84c" }} />
                ) : (
                  <div style={{ width: 180, height: 180, background: "#f5f0e8", border: "1px dashed #c9a84c", display: "flex", alignItems: "center", justifyContent: "center", color: "#9a7530", fontFamily: "Cinzel, serif", fontSize: "0.8rem" }}>
                    NO PHOTO
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="panel">
              <div className="panel-head">{t("members.personal")}</div>
              <div className="panel-body">
                {[
                  { label: t("members.firstName"), value: form.first_name },
                  { label: t("members.lastName"), value: form.last_name },
                  { label: t("members.dateOfBirth"), value: form.date_of_birth },
                  { label: t("members.passport"), value: form.passport_number },
                  { label: t("members.address"), value: form.address },
                  { label: t("common.phone"), value: form.phone },
                  { label: t("common.email"), value: form.email },
                  { label: t("members.profession"), value: form.profession },
                ].map((kv) => (
                  <div key={kv.label} className="kv-row">
                    <span className="kv-label">{kv.label}</span>
                    <span className="kv-value">{kv.value || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel">
              <div className="panel-head">{t("members.masonic")}</div>
              <div className="panel-body">
                {[
                  { label: t("members.degree"), value: (() => {
                    if (!form.degree) return form.grade || "—";
                    const g = grades.find((x) => x.degree === form.degree);
                    const lng = normalizeLangCode(i18n.language || "ro");
                    const localized = g ? (g[`name_${lng}`] || g[`name_${lng.slice(0, 2)}`] || g.name_en || g.name_ro) : (form.grade || "");
                    return `${form.degree}° — ${localized}`;
                  })() },
                  { label: t("members.passportNumber",t("memberProfile.passport")), value: form.passport_number },
                  { label: t("common.status"), value: form.status },
                  { label: t("members.dateInitiated"), value: form.date_initiated },
                  { label: t("members.datePromoted"), value: form.date_promoted },
                  { label: t("members.lodgeOffice",t("memberProfile.lodge_officer")), value: form.lodge_office ? (LODGE_OFFICE_TKEY[form.lodge_office] ? t(LODGE_OFFICE_TKEY[form.lodge_office]) : form.lodge_office) : "" },
                  { label: t("members.grandOffice", "Funcție în Marea Lojă"), value: form.grand_office ? (LODGE_OFFICE_TKEY[form.grand_office] ? t(LODGE_OFFICE_TKEY[form.grand_office]) : form.grand_office) : "" },
                ].map((kv) => (
                  <div key={kv.label} className="kv-row">
                    <span className="kv-label">{kv.label}</span>
                    <span className="kv-value">{kv.value || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
            {!isNew && form.id && (
              <OfficeHistoryPanel
                memberId={form.id}
                items={form.office_history || []}
                isAdmin={isAdmin}
                onChanged={(items) => setForm((f) => ({ ...f, office_history: items }))}
              />
            )}
            {isAdmin && form.nfc_token && (
              <NfcPassportPanel token={form.nfc_token} />
            )}
            {isAdmin && form.id && form.email && (
              <MobileAppPanel
                memberId={form.id}
                memberEmail={form.email}
                memberScope={form.network_scope || "lodge"}
                memberFirstName={form.first_name}
                memberLastName={form.last_name}
                consentStatus={form.consent_status}
                isSister={memberIsSister}
              />
            )}
            {isAdmin && form.id && <MemberDocsPanel memberId={form.id} />}
            {isAdmin && form.id && <EduDiplomasPanel memberId={form.id} />}
            {(form.notes || form.additional_info) && (
              <div className="panel">
                <div className="panel-head">{t("members.additionalInfo")}</div>
                <div className="panel-body">
                  {form.notes && <p style={{ whiteSpace: "pre-wrap", marginBottom: "0.8rem" }}>{form.notes}</p>}
                  {form.additional_info && <p style={{ whiteSpace: "pre-wrap", color: "#34322d" }}>{form.additional_info}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pendingTransfer && (
        <div className="dialog-overlay" onClick={() => setPendingTransfer(null)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="duplicate-transfer-dialog">
            <h2 className="dialog-title">{t("transfers.duplicateTitle", "Mason existent")}</h2>
            <p style={{ color: "#34322d", marginBottom: "0.8rem" }}>
              {t("transfers.duplicateMessage", "Acest mason există deja la altă lojă din sistem.")}
            </p>
            <div
              style={{
                background: "#fdf4e0",
                border: "1px solid #c9a84c",
                padding: "0.8rem 1rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", color: "#0d1b2a" }}>
                {pendingTransfer.detail.member?.first_name} {pendingTransfer.detail.member?.last_name}
              </div>
              <div className="kv-label" style={{ marginTop: 4 }}>
                {pendingTransfer.detail.member?.grade} · {pendingTransfer.detail.member?.status}
              </div>
              <div className="kv-label" style={{ marginTop: 4 }}>
                {t("attendance.visitorFrom")}:{" "}
                <strong>{pendingTransfer.detail.home_lodge?.name || "—"}</strong>
              </div>
            </div>
            <p style={{ fontSize: "0.85rem", color: "#34322d", marginBottom: "1.2rem" }}>
              {t("transfers.duplicateHelp", "La confirmare, se va trimite o cerere de transfer către loja origine. Membrul va apărea în lista ta doar după ce loja origine aprobă.")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => setPendingTransfer(null)}
                data-testid="duplicate-transfer-cancel"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary-gold"
                onClick={goToTransfers}
                disabled={saving}
                data-testid="duplicate-transfer-confirm"
              >
                {saving ? "..." : t("transfers.requestTransferBtn", "SOLICITĂ TRANSFER")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-save prompt: just created a member with email — offer to
          immediately issue mobile credentials. Avoids the "I forgot to
          generate the password" / "I confused this member with another"
          class of bugs the user has been hitting. */}
      {newMemberPrompt && !postSaveCreds && (
        <div style={modalOverlay} data-testid="post-save-prompt">
          <div style={{ ...modalCard, maxWidth: 480 }}>
            <div style={{ background: "#0d1b2a", color: "#fdf4e0", padding: "0.7rem 0.9rem", marginBottom: 14, borderLeft: "3px solid #c9a84c" }}>
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.7rem", letterSpacing: "1.5px", color: "#c9a84c", marginBottom: 4 }}>
                {t("members.postSaveTitle", "MEMBRU SALVAT CU SUCCES")}
              </p>
              <p style={{ fontSize: "0.95rem", fontWeight: 700 }}>
                Fr. {newMemberPrompt.firstName} {newMemberPrompt.lastName}
              </p>
              <p style={{ fontSize: "0.78rem", fontFamily: "monospace", marginTop: 2, opacity: 0.85 }}>
                {newMemberPrompt.email}
              </p>
            </div>
            <p style={{ fontSize: "0.92rem", color: "#0d1b2a", marginBottom: 12 }}>
              {t("members.postSavePrompt", "Generezi acum credentialele pentru aplicația mobilă a fratelui? Le poți copia și trimite imediat prin WhatsApp / SMS.")}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => {
                  setNewMemberPrompt(null);
                  navigate(`/members/${newMemberPrompt.id}`, { replace: true });
                }}
                data-testid="post-save-skip"
              >
                {t("members.postSaveSkip", "MAI TÂRZIU")}
              </button>
              <button
                type="button"
                className="btn-primary-gold flex items-center gap-1"
                onClick={async () => {
                  setIssuingNewMember(true);
                  try {
                    const { data } = await api.post(`/admin/members/${newMemberPrompt.id}/issue-activation`);
                    setPostSaveCreds(data);
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setIssuingNewMember(false);
                  }
                }}
                disabled={issuingNewMember}
                data-testid="post-save-generate"
              >
                <Smartphone size={12} /> {issuingNewMember ? "..." : t("members.postSaveGenerate", "GENEREAZĂ ACUM")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show the freshly issued credentials — same identity banner as in
          MobileAppPanel so the admin can never confuse who they belong to. */}
      {newMemberPrompt && postSaveCreds && (
        <div style={modalOverlay} data-testid="post-save-creds">
          <div style={{ ...modalCard, maxWidth: 520 }}>
            <div style={{ background: "#0d1b2a", color: "#fdf4e0", padding: "0.7rem 0.9rem", marginBottom: 14, borderLeft: "3px solid #c9a84c" }}>
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.7rem", letterSpacing: "1.5px", color: "#c9a84c", marginBottom: 4 }}>
                {t("members.postSaveCredsTitle", "CREDENȚIALE PENTRU")}
              </p>
              <p style={{ fontSize: "0.95rem", fontWeight: 700 }}>
                Fr. {newMemberPrompt.firstName} {newMemberPrompt.lastName}
              </p>
            </div>
            <div style={{ background: "#fdf4e0", border: "1px solid #c9a84c", padding: "0.85rem 1rem" }}>
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.85rem", color: "#0d1b2a", letterSpacing: "0.1em", marginBottom: 8 }}>
                {t("members.postSaveOnce", "VIZIBILE O SINGURĂ DATĂ")}
              </p>
              <div style={{ display: "flex", gap: 10, padding: "4px 0" }}>
                <span style={{ fontFamily: "Cinzel, serif", fontSize: "0.7rem", letterSpacing: 1.5, color: "#8a6f1c", minWidth: 70 }}>{t("members.postSaveEmail", "EMAIL:")}</span>
                <span style={{ fontFamily: "monospace", fontSize: "0.88rem", color: "#0d1b2a", wordBreak: "break-all" }}>{postSaveCreds.username}</span>
              </div>
              <div style={{ display: "flex", gap: 10, padding: "4px 0" }}>
                <span style={{ fontFamily: "Cinzel, serif", fontSize: "0.7rem", letterSpacing: 1.5, color: "#8a6f1c", minWidth: 70 }}>{t("members.postSavePassword", "PAROLĂ:")}</span>
                <span style={{ fontFamily: "monospace", fontSize: "0.88rem", color: "#0d1b2a", wordBreak: "break-all" }}>{postSaveCreds.temp_password}</span>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  className="btn-primary-gold flex items-center gap-1"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`Email: ${postSaveCreds.username}\nParolă: ${postSaveCreds.temp_password}`);
                      toast.success(t("common.copied", "Copiate"));
                    } catch { /* ignore */ }
                  }}
                  data-testid="post-save-copy"
                >
                  <Copy size={12} /> {t("mobileApp.copyAll", "COPIAZĂ TOATE")}
                </button>
                <button
                  type="button"
                  className="btn-outline-gold"
                  onClick={() => {
                    setNewMemberPrompt(null);
                    setPostSaveCreds(null);
                    navigate(`/members/${newMemberPrompt.id}`, { replace: true });
                  }}
                  data-testid="post-save-close"
                >
                  {t("common.close", "ÎNCHIDE")}
                </button>
              </div>
              <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 8, color: "#8a2a2a", fontStyle: "italic" }}>
                {t("credentials.warnOneTime")}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Signature pad — modal opened by the SIGN NOW button on the
          member's profile (NOT inside MobileAppPanel — uses
          showSignaturePad/setShowSignaturePad/submitDigitalSignature
          which live on this component). Renders a 480×180 canvas the
          admin signs with mouse or finger; on save the base64 PNG is
          sent to /api/upload/signature/{id}/digital which strips the
          white background. */}
      {showSignaturePad ? (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(13,27,42,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 60, padding: "1rem",
          }}
          data-testid="signature-pad-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSignaturePad(false); }}
        >
          <div className="gold-card" style={{ padding: "1.2rem", maxWidth: 540, width: "100%" }}>
            <h3 style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", marginBottom: 8 }}>
              {t("memberProfile.capture_signature")}
            </h3>
            <SignaturePad
              width={480}
              height={180}
              lang={i18n.language}
              onSave={submitDigitalSignature}
              onCancel={() => setShowSignaturePad(false)}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NfcPassportPanel({ token }) {
  const { t } = useTranslation();
  const url = `${window.location.origin}/check-in/${token}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("attendance.tokenCopied"));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="panel" data-testid="nfc-passport-panel">
      <div className="panel-head">
        <Smartphone size={13} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
        {t("attendance.checkInUrl")}
      </div>
      <div className="panel-body">
        <code
          style={{
            display: "block",
            background: "#fdf4e0",
            border: "1px solid #c9a84c",
            padding: "0.55rem 0.7rem",
            fontSize: "0.78rem",
            wordBreak: "break-all",
            color: "#0d1b2a",
            marginBottom: "0.75rem",
          }}
          data-testid="nfc-url"
        >
          {url}
        </code>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="btn-outline-gold flex items-center gap-1" onClick={copy} data-testid="copy-nfc-url">
            <Copy size={11} /> {t("attendance.copyUrl")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberDocsPanel({ memberId }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [docs, setDocs] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/members/${memberId}/documents`);
      setDocs(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [memberId]);

  React.useEffect(() => {
    if (memberId) load();
  }, [memberId, load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.pdf$/i, ""));
    try {
      await api.post(`/members/${memberId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("attendance.uploadPdf"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeDoc = async (d) => {
    const ok = await confirm({ title: t("common.delete"), message: `${t("common.delete")}: ${d.title}?` });
    if (!ok) return;
    try {
      await api.delete(`/members/${memberId}/documents/${d.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtSize = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="panel" data-testid="member-docs-panel">
      <div className="panel-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{t("attendance.memberDocs")}</span>
        <span className="kv-label" style={{ color: "rgba(232,201,122,0.6)", fontSize: "0.62rem" }}>
          {docs.length}
        </span>
      </div>
      <div className="panel-body">
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onPick}
          style={{ display: "none" }}
          data-testid="member-doc-file-input"
        />
        <button
          type="button"
          className="btn-outline-gold flex items-center gap-2 mb-3"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="upload-member-doc-btn"
        >
          <Upload size={12} /> {uploading ? t("attendance.uploading") : t("attendance.uploadPdf")}
        </button>
        {docs.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "#858481", fontSize: "0.85rem" }}>{t("attendance.noDocs")}</p>
        ) : (
          <ul style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }} data-testid="member-docs-list">
            {docs.map((d) => (
              <li
                key={d.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.6rem",
                  padding: "0.5rem 0.7rem",
                  background: "#fff",
                  border: "1px solid var(--cream-200)",
                  fontSize: "0.85rem",
                }}
                data-testid={`member-doc-${d.id}`}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.82rem" }}>
                    {d.title}
                  </span>
                  <span className="kv-label" style={{ display: "block", fontSize: "0.6rem" }}>
                    {fmtSize(d.size)} · {new Date(d.uploaded_at).toLocaleDateString()}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn-outline-gold"
                  onClick={() => downloadUrl(`${API}/members/${memberId}/documents/${d.id}/download${authQuery()}`, d.filename || d.title)}
                  data-testid={`download-member-doc-${d.id}`}
                  style={{ textDecoration: "none", background: "transparent", border: "1px solid #c9a84c", cursor: "pointer" }}
                >
                  {t("attendance.downloadDoc")}
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => removeDoc(d)}
                  data-testid={`delete-member-doc-${d.id}`}
                >
                  <Trash2 size={11} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function EduDiplomasPanel({ memberId }) {
  const { t } = useTranslation();
  const [dips, setDips] = React.useState([]);

  React.useEffect(() => {
    if (!memberId) return;
    api.get(`/members/${memberId}/edu-diplomas`)
      .then(({ data }) => setDips(data))
      .catch(() => setDips([]));
  }, [memberId]);

  return (
    <div className="panel" data-testid="edu-diplomas-panel">
      <div className="panel-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{t("memberProfile.eduDiplomas", "Diplome de educație")}</span>
        <span className="kv-label" style={{ color: "rgba(232,201,122,0.6)", fontSize: "0.62rem" }}>{dips.length}</span>
      </div>
      <div className="panel-body">
        {dips.length === 0 ? (
          <p style={{ fontStyle: "italic", color: "#858481", fontSize: "0.85rem" }} data-testid="edu-diplomas-empty">
            {t("memberProfile.eduDiplomasEmpty", "Nicio diplomă de educație încă.")}
          </p>
        ) : (
          <ul style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }} data-testid="edu-diplomas-list">
            {dips.map((d) => (
              <li
                key={d.id}
                style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0.7rem", background: "#fff", border: "1px solid var(--cream-200)", fontSize: "0.85rem" }}
                data-testid={`edu-diploma-${d.id}`}
              >
                <GraduationCap size={16} style={{ color: "#9a7530", flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.82rem" }}>{d.title}</span>
                  <span className="kv-label" style={{ display: "block", fontSize: "0.6rem" }}>
                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn-outline-gold"
                  onClick={() => downloadUrl(`${API}/members/${memberId}/edu-diplomas/${d.id}/download${authQuery()}`, d.filename || d.title)}
                  data-testid={`download-edu-diploma-${d.id}`}
                  style={{ textDecoration: "none", background: "transparent", border: "1px solid #c9a84c", cursor: "pointer" }}
                >
                  {t("attendance.downloadDoc")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function MobileAppPanel({ memberId, memberEmail, memberScope, memberFirstName, memberLastName, consentStatus, isSister }) {
  const { t } = useTranslation();
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState(memberScope || "lodge");

  // CRITICAL: reset creds whenever the admin opens a different member's
  // profile. Without this, switching between members could show stale
  // credentials from a previous session and the admin might confuse them.
  useEffect(() => {
    setCreds(null);
    setScope(memberScope || "lodge");
  }, [memberId, memberScope]);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${memberId}/issue-activation`);
      setCreds(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const [consentBusy, setConsentBusy] = useState(false);
  const [localConsent, setLocalConsent] = useState(null);
  const consent = localConsent || consentStatus; // 'signed' | 'pending' | undefined (legacy)
  const sendConsent = async () => {
    setConsentBusy(true);
    try {
      const { data } = await api.post(`/members/${memberId}/consent/send`);
      setLocalConsent("pending");
      if (data.email_sent) {
        toast.success(t("consentAdmin.sentOk", "Invitația de semnare a fost trimisă pe email."));
      } else {
        try { await navigator.clipboard.writeText(data.link); } catch { /* clipboard blocked */ }
        toast.warning(
          `${t("consentAdmin.emailFailed", "Emailul nu a putut fi livrat (serviciul de email nu are domeniul verificat). Link-ul de semnare a fost copiat — trimite-l manual fratelui:")} ${data.link}`,
          { duration: 15000 },
        );
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConsentBusy(false);
    }
  };

  const updateScope = async (newScope) => {
    setScope(newScope);
    try {
      await api.patch(`/admin/members/${memberId}/network-scope`, { network_scope: newScope });
      toast.success(t("mobileApp.scopeSaved", "Acces conexiuni actualizat"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} ${t("mobileApp.copiedSuffix", "copiat")}`);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="panel" data-testid="mobile-app-panel">
      <div className="panel-head">
        <Smartphone size={13} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
        {t("mobileApp.title", "APLICAȚIE MOBILĂ")}
      </div>
      <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <div>
          <label className="label-lodge">{t("mobileApp.networkScope", "Acces conexiuni rețea")}</label>
          <select
            className="input-lodge"
            value={scope}
            onChange={(e) => updateScope(e.target.value)}
            data-testid="member-network-scope"
          >
            <option value="lodge">{t("mobileApp.scopeLodge", "Doar lojă")}</option>
            <option value="national">{t("mobileApp.scopeNational", "Național (toată obediența)")}</option>
            <option value="world">{t("mobileApp.scopeWorld", "Mondial (toate lojile)")}</option>
          </select>
          <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 2 }}>
            {t("mobileApp.scopeHelp", "Cu ce frați se poate conecta acest membru din aplicația mobilă.")}
          </p>
        </div>

        {!creds ? (
          <div>
            {/* Identity banner — shows EXACTLY which member will receive the
                credentials. Prevents the "I generated for X but copied for Y"
                confusion users have reported. */}
            <div
              style={{
                background: "#0d1b2a",
                color: "#fdf4e0",
                padding: "0.7rem 0.9rem",
                marginBottom: "0.6rem",
                borderLeft: "3px solid #c9a84c",
              }}
              data-testid="mobile-target-banner"
            >
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.7rem", letterSpacing: "1.5px", color: "#c9a84c", marginBottom: 4 }}>
                {t("mobileApp.willGenerateFor", "VEI GENERA CREDENȚIALE PENTRU")}
              </p>
              <p style={{ fontSize: "0.95rem", fontWeight: 700 }}>
                {isSister ? t("mobileAdmin.sisterShort", "Sr.") : t("mobileAdmin.brotherShort", "Fr.")} {memberFirstName || "?"} {memberLastName || ""}
              </p>
              <p style={{ fontSize: "0.78rem", fontFamily: "monospace", color: "#fdf4e0", marginTop: 2, opacity: 0.85 }}>
                {memberEmail || (
                  <span style={{ color: "#ff9f9f", fontStyle: "italic" }}>
                    {t("mobileApp.noEmailWarn", "⚠ Acest membru nu are email. Adaugă-i unul în profil înainte de a genera parola.")}
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: "0.7rem" }} data-testid="consent-status-row">
              <span style={consent === "signed"
                ? { color: "#3e6a3c", border: "1px solid #3e6a3c", background: "#e9f3e8", padding: "2px 8px", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em" }
                : consent === "pending"
                  ? { color: "#8a6f1c", border: "1px solid #c9a84c", background: "#fdf4e0", padding: "2px 8px", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em" }
                  : { color: "#6b6a65", border: "1px solid #9a9a9a", padding: "2px 8px", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em" }}>
                {consent === "signed" ? t("consentAdmin.signed", "ACORD T&C/GDPR SEMNAT")
                  : consent === "pending" ? t("consentAdmin.pending", "ÎN AȘTEPTAREA SEMNĂRII")
                    : t("consentAdmin.notSent", "ACORD NESEMNAT")}
              </span>
              {consent !== "signed" && (
                <button type="button" className="btn-outline-gold flex items-center gap-1" style={{ fontSize: "0.66rem", padding: "4px 10px" }}
                  onClick={sendConsent} disabled={consentBusy || !memberEmail} data-testid="consent-send-btn">
                  <Send size={11} /> {consentBusy ? "..." : consent === "pending" ? t("consentAdmin.resend", "RETRIMITE INVITAȚIA") : t("consentAdmin.send", "TRIMITE SPRE SEMNARE")}
                </button>
              )}
            </div>
            <p style={{ fontSize: "0.85rem", color: "#34322d", marginBottom: "0.5rem" }}>
              {t("mobileApp.inviteHelp", "Generează parola temporară pentru aplicația mobilă. Fratele se conectează direct cu emailul lui și parola de mai jos. Vei primi datele O SINGURĂ DATĂ — copiază-le și trimite-le prin WhatsApp / SMS / email personal.")}
            </p>
            <button
              type="button"
              className="btn-primary-gold flex items-center gap-1"
              onClick={issue}
              disabled={busy || !memberEmail}
              data-testid="issue-mobile-invite"
            >
              <Smartphone size={12} /> {busy ? "..." : t("mobileApp.issue", "GENEREAZĂ PAROLĂ MOBIL")}
            </button>
          </div>
        ) : (
          <div style={{ background: "#fdf4e0", border: "1px solid #c9a84c", padding: "0.85rem 1rem" }} data-testid="mobile-invite-creds">
            {/* Same identity banner shown ABOVE the freshly-issued
                credentials so the admin sees who they belong to before
                copying anything. */}
            <div style={{ background: "#0d1b2a", color: "#fdf4e0", padding: "0.55rem 0.8rem", marginBottom: 10, borderLeft: "3px solid #c9a84c" }}>
              <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.65rem", letterSpacing: "1.5px", color: "#c9a84c" }}>
                {t("mobileApp.credsForLabel", "CREDENȚIALE PENTRU")}
              </p>
              <p style={{ fontSize: "0.9rem", fontWeight: 700 }}>
                {isSister ? t("mobileAdmin.sisterShort", "Sr.") : t("mobileAdmin.brotherShort", "Fr.")} {memberFirstName} {memberLastName}
              </p>
            </div>
            <p style={{ fontFamily: "Cinzel, serif", fontSize: "0.85rem", color: "#0d1b2a", letterSpacing: "0.1em", marginBottom: 8 }}>
              {t("mobileApp.credsTitle", "CREDENTIALE MOBIL — VIZIBILE O SINGURĂ DATĂ")}
            </p>
            <CredRow label={t("mobileApp.username", "Email (utilizator)")} value={creds.username} onCopy={copy} testid="mobile-username" />
            <CredRow label={t("mobileApp.password", "Parolă temporară")} value={creds.temp_password} onCopy={copy} testid="mobile-password" />
            <div className="flex gap-2 flex-wrap" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn-outline-gold flex items-center gap-1"
                onClick={() => copy(
                  `${t("mobileApp.emailLabel", "Email:")} ${creds.username}\n${t("mobileApp.passwordLabel", "Parolă:")} ${creds.temp_password}`,
                  t("mobileApp.copyAllLabel", "Toate datele"),
                )}
                data-testid="copy-mobile-all"
              >
                <Copy size={11} /> {t("mobileApp.copyAll", "COPIAZĂ TOATE")}
              </button>
              <button
                type="button"
                className="btn-outline-gold"
                onClick={() => setCreds(null)}
                data-testid="hide-mobile-creds"
              >
                {t("common.close", "ÎNCHIDE")}
              </button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.68rem", marginTop: 8, color: "#8a2a2a", fontStyle: "italic" }}>
              ⚠️ {t("mobileApp.warning", "După închidere nu mai poți recupera parola — doar regenera una nouă. La prima conectare, fratele va fi obligat să o schimbe.")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function CredRow({ label, value, onCopy, testid }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div className="kv-label" style={{ fontSize: "0.65rem" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <code
          style={{
            flex: 1, minWidth: 0, fontSize: "0.78rem",
            background: "#fff", border: "1px solid #ead8a8",
            padding: "0.35rem 0.55rem", color: "#0d1b2a",
            wordBreak: "break-all",
          }}
          data-testid={testid}
        >
          {value}
        </code>
        <button
          type="button"
          className="btn-outline-gold"
          style={{ padding: "0.35rem 0.55rem", flexShrink: 0 }}
          onClick={() => onCopy(value, label)}
          data-testid={`${testid}-copy`}
        >
          <Copy size={11} />
        </button>
      </div>
    </div>
  );
}

